import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
    Building, Search, Trash2, Edit, Loader2, Plus, Users, FolderKanban, 
    AlertTriangle, UserPlus, Crown, Eye, X, Mail, RefreshCw, Clock,
    ChevronDown, ChevronRight, Check, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Menu catalog — must match backend/core/menu_catalog.py
const MENU_CATALOG = {
    company_group: {
        label: "Company Group",
        items: {
            guest_messages: "Guest Message",
            company: "Company Setting",
        }
    },
    main: {
        label: "Main",
        items: {
            dashboard: "Dashboard",
            calendar: "Calendar",
            mail: "Mail",
            file: "File",
        }
    },
    task_manager: {
        label: "Task Manager",
        items: {
            my_task: "My Task",
            review_by_me: "Review by Me",
            assign_by_me: "Assign By Me",
            tasks: "Task",
        }
    },
    discussion: {
        label: "Discussion",
        items: {
            channel: "Channel",
            direct_message: "Direct Message",
        }
    },
    project_manager: {
        label: "Project Manager Area",
        items: {
            daily_standup: "Daily Standup",
        }
    },
    crm_area: {
        label: "CRM Area",
        items: {
            partnerships: "Contact",
            leads: "Lead",
            clients: "Client",
            projects: "Project",
        }
    },
    admin_area: {
        label: "Admin Area",
        items: {
            team: "Team",
            ai_agents: "AI Agents",
            tracking_member: "Tracking Member",
            dashboard_admin: "Dashboard Admin",
            deleted_tasks: "Deleted Tasks",
            settings: "Settings",
        }
    },
};

// Build full enabled_menus (all items enabled)
const FULL_ENABLED_MENUS = {};
Object.entries(MENU_CATALOG).forEach(([section, data]) => {
    FULL_ENABLED_MENUS[section] = Object.keys(data.items);
});

// Helper: convert enabled_menus to toggle state for UI
function enabledMenusToToggles(enabledMenus) {
    const toggles = {};
    Object.entries(MENU_CATALOG).forEach(([section, data]) => {
        toggles[section] = {};
        Object.keys(data.items).forEach((key) => {
            // If null/undefined → all enabled (backward compat), else check
            if (!enabledMenus) {
                toggles[section][key] = true;
            } else {
                toggles[section][key] = (enabledMenus[section] || []).includes(key);
            }
        });
    });
    return toggles;
}

// Helper: convert toggle state back to enabled_menus format
function togglesToEnabledMenus(toggles) {
    const result = {};
    Object.entries(toggles).forEach(([section, items]) => {
        const enabled = Object.entries(items)
            .filter(([_, checked]) => checked)
            .map(([key]) => key);
        if (enabled.length > 0) {
            result[section] = enabled;
        }
    });
    return result;
}

// Helper: check if all items in a section are checked
function isSectionAllChecked(toggles, section) {
    return Object.values(toggles[section] || {}).every(Boolean);
}

// Helper: check if no items in a section are checked
function isSectionNoneChecked(toggles, section) {
    return Object.values(toggles[section] || {}).every(v => !v);
}

export default function CompanyManager() {
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [createModal, setCreateModal] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, company: null });
    const [deleteModal, setDeleteModal] = useState({ open: false, company: null });
    const [memberModal, setMemberModal] = useState({ open: false, company: null });
    const [wsModal, setWsModal] = useState({ open: false, company: null });
    const [wsForm, setWsForm] = useState({ name: '', description: '', admin_user_id: '' });
    const [wsMembers, setWsMembers] = useState([]);
    const [creatingWs, setCreatingWs] = useState(false);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [addMemberSearch, setAddMemberSearch] = useState('');
    const [migrating, setMigrating] = useState(false);

    const [createForm, setCreateForm] = useState({ name: '', description: '', owner_email: '' });
    const [createMenuToggles, setCreateMenuToggles] = useState(() => enabledMenusToToggles(null));
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const [editMenuToggles, setEditMenuToggles] = useState(() => enabledMenusToToggles(null));

    // Collapsed state for menu sections
    const [collapsedSections, setCollapsedSections] = useState({});

    const getToken = () => localStorage.getItem('superadmin_token');

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE}/api/superadmin/companies`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setCompanies(res.data);
        } catch (err) {
            toast.error('Failed to load companies');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/users`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setUsers(res.data);
        } catch (err) {
            console.error('Failed to load users');
        }
    };

    useEffect(() => {
        fetchCompanies();
        fetchUsers();
    }, []);

    // Check if any company needs migration (enabled_menus is null)
    const companiesNeedingMigration = companies.filter(c => !c.enabled_menus);

    const handleCreate = async () => {
        if (!createForm.name || !createForm.owner_email) {
            toast.error('Name and Owner Email are required');
            return;
        }
        try {
            const enabled_menus = togglesToEnabledMenus(createMenuToggles);
            await axios.post(`${API_BASE}/api/superadmin/companies`, {
                name: createForm.name,
                description: createForm.description,
                owner_email: createForm.owner_email,
                enabled_menus: Object.keys(enabled_menus).length > 0 ? enabled_menus : null,
            }, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Company created');
            setCreateModal(false);
            setCreateForm({ name: '', description: '', owner_email: '' });
            setCreateMenuToggles(enabledMenusToToggles(null));
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create company');
        }
    };

    const handleEdit = async () => {
        try {
            const enabled_menus = togglesToEnabledMenus(editMenuToggles);
            await axios.put(`${API_BASE}/api/superadmin/companies/${editModal.company.id}`, {
                ...editForm,
                enabled_menus: Object.keys(enabled_menus).length > 0 ? enabled_menus : null,
            }, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Company updated');
            setEditModal({ open: false, company: null });
            fetchCompanies();
        } catch (err) {
            toast.error('Failed to update company');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_BASE}/api/superadmin/companies/${deleteModal.company.id}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Company deleted');
            setDeleteModal({ open: false, company: null });
            fetchCompanies();
        } catch (err) {
            toast.error('Failed to delete company');
        }
    };

    const handleMigrateMenus = async () => {
        setMigrating(true);
        try {
            const res = await axios.post(`${API_BASE}/api/superadmin/companies/migrate-menus`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success(`Migrasi berhasil: ${res.data.migrated} company diperbarui`);
            fetchCompanies();
        } catch (err) {
            toast.error('Gagal migrasi menu');
        } finally {
            setMigrating(false);
        }
    };

    const fetchMembers = async (companyId) => {
        setLoadingMembers(true);
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/companies/${companyId}/members`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setMembers(res.data);
        } catch (err) {
            toast.error('Failed to load members');
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleAddMember = async (userId) => {
        if (!memberModal.company) return;
        try {
            await axios.post(`${API_BASE}/api/superadmin/companies/${memberModal.company.id}/members`, 
                { user_id: userId },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            toast.success('Member added');
            fetchMembers(memberModal.company.id);
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add member');
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!memberModal.company) return;
        try {
            await axios.delete(`${API_BASE}/api/superadmin/companies/${memberModal.company.id}/members/${userId}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Member removed');
            fetchMembers(memberModal.company.id);
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove member');
        }
    };

    const openWsModal = async (company) => {
        setWsForm({ name: '', description: '', admin_user_id: '' });
        setWsMembers([]);
        setWsModal({ open: true, company });
        try {
            const res = await axios.get(`${API_BASE}/api/superadmin/companies/${company.id}/members`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setWsMembers(res.data || []);
        } catch (err) {
            // dropdown admin opsional — abaikan kegagalan fetch member
        }
    };

    const handleCreateWorkspace = async () => {
        if (!wsForm.name.trim()) { toast.error('Nama workspace wajib diisi'); return; }
        if (!wsModal.company) return;
        setCreatingWs(true);
        try {
            await axios.post(
                `${API_BASE}/api/superadmin/companies/${wsModal.company.id}/workspaces`,
                wsForm,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            toast.success('Workspace dibuat');
            setWsModal({ open: false, company: null });
            setWsForm({ name: '', description: '', admin_user_id: '' });
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal membuat workspace');
        } finally {
            setCreatingWs(false);
        }
    };

    const filteredCompanies = companies.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.owner_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.owner_email || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleResendInvitation = async (companyId) => {
        try {
            await axios.post(`${API_BASE}/api/superadmin/companies/${companyId}/resend-invitation`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Invitation resent successfully');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to resend invitation');
        }
    };

    const [syncingCompanyId, setSyncingCompanyId] = useState(null);

    const handleSyncMembers = async (companyId) => {
        setSyncingCompanyId(companyId);
        try {
            const res = await axios.post(`${API_BASE}/api/superadmin/companies/${companyId}/sync-members`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            const { added, skipped, errors } = res.data;
            if (errors && errors.length > 0) {
                toast.warning(`Sync selesai: ${added} ditambah, ${skipped} sudah ada, ${errors.length} error`);
            } else {
                toast.success(`Sync berhasil: ${added} member ditambahkan, ${skipped} sudah ada`);
            }
            fetchCompanies();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal sync members');
        } finally {
            setSyncingCompanyId(null);
        }
    };

    const toggleSectionAll = (section, toggles, setToggles) => {
        const allChecked = isSectionAllChecked(toggles, section);
        setToggles(prev => ({
            ...prev,
            [section]: Object.keys(prev[section]).reduce((acc, key) => {
                acc[key] = !allChecked;
                return acc;
            }, {})
        }));
    };

    const toggleMenuItem = (section, key, setToggles) => {
        setToggles(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [key]: !prev[section][key]
            }
        }));
    };

    // Render menu toggles component
    const MenuToggles = ({ toggles, setToggles, collapsed, setCollapsed }) => (
        <div className="space-y-2 mt-2">
            <Label className="text-sm font-semibold text-slate-300">Enabled Menus</Label>
            <p className="text-xs text-slate-400">Pilih menu yang tersedia untuk company ini. Kosongkan section untuk menyembunyikan seluruh section.</p>
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {Object.entries(MENU_CATALOG).map(([section, data]) => {
                    const allChecked = isSectionAllChecked(toggles, section);
                    const noneChecked = isSectionNoneChecked(toggles, section);
                    const isCollapsed = collapsed[section];
                    return (
                        <div key={section} className="border border-slate-600 rounded-lg">
                            <div
                                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-700/50"
                                onClick={() => setCollapsed(prev => ({ ...prev, [section]: !prev[section] }))}
                            >
                                <button
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                                        allChecked ? 'bg-amber-500 border-amber-500' : noneChecked ? 'border-slate-500' : 'border-amber-400 bg-amber-400/20'
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSectionAll(section, toggles, setToggles);
                                    }}
                                >
                                    {allChecked && <Check className="w-3 h-3 text-black" />}
                                    {!allChecked && !noneChecked && <span className="w-2 h-0.5 bg-amber-400 rounded" />}
                                </button>
                                <span className="text-sm font-medium text-white flex-1">{data.label}</span>
                                <span className="text-xs text-slate-400">
                                    {Object.values(toggles[section]).filter(Boolean).length}/{Object.keys(toggles[section]).length}
                                </span>
                                {isCollapsed ? (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                ) : (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                )}
                            </div>
                            {!isCollapsed && (
                                <div className="px-3 pb-2 flex flex-wrap gap-2">
                                    {Object.entries(data.items).map(([key, label]) => (
                                        <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!toggles[section]?.[key]}
                                                onChange={() => toggleMenuItem(section, key, setToggles)}
                                                className="w-3.5 h-3.5 rounded border-slate-500 text-amber-500 focus:ring-amber-500 bg-slate-700"
                                            />
                                            <span className={toggles[section]?.[key] ? 'text-white' : 'text-slate-500'}>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const existingMemberIds = members.map(m => m.user_id);
    const availableUsers = users.filter(u => 
        !existingMemberIds.includes(u.id) &&
        (u.name?.toLowerCase().includes(addMemberSearch.toLowerCase()) ||
         u.email?.toLowerCase().includes(addMemberSearch.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Building className="w-6 h-6 text-amber-500" />
                        Company Manager
                    </h1>
                    <p className="text-slate-400 mt-1">Manage companies and their workspaces</p>
                </div>
                <div className="flex items-center gap-2">
                    {companiesNeedingMigration.length > 0 && (
                        <Button
                            onClick={handleMigrateMenus}
                            disabled={migrating}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                            Migrasi Menu ({companiesNeedingMigration.length})
                        </Button>
                    )}
                    <Button onClick={() => setCreateModal(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
                        <Plus className="w-4 h-4 mr-2" /> Create Company
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
            ) : (
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-700 hover:bg-transparent">
                                    <TableHead className="text-slate-400">Company</TableHead>
                                    <TableHead className="text-slate-400">Owner</TableHead>
                                    <TableHead className="text-slate-400">Members</TableHead>
                                    <TableHead className="text-slate-400">Workspaces</TableHead>
                                    <TableHead className="text-slate-400">Menus</TableHead>
                                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCompanies.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                                            No companies found
                                        </TableCell>
                                    </TableRow>
                                ) : filteredCompanies.map((company) => {
                                    const menuCount = company.enabled_menus
                                        ? Object.values(company.enabled_menus).reduce((sum, items) => sum + items.length, 0)
                                        : 'All';
                                    return (
                                        <TableRow key={company.id} className="border-slate-700 hover:bg-slate-700/50">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                                         style={{ backgroundColor: company.color || '#2F6F63' }}>
                                                        {company.name[0]?.toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-white">{company.name}</div>
                                                        {company.description && (
                                                            <div className="text-xs text-slate-400 truncate max-w-[200px]">{company.description}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {company.pending_invitation ? (
                                                    <div>
                                                        <div className="flex items-center gap-1 text-amber-400">
                                                            <Clock className="w-3 h-3" />
                                                            <span className="text-sm font-medium">Pending Invitation</span>
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <Mail className="w-3 h-3 text-slate-400" />
                                                            <span className="text-xs text-slate-400">{company.invitation_email || company.owner_email}</span>
                                                        </div>
                                                        <button onClick={() => handleResendInvitation(company.id)}
                                                            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-1">
                                                            <RefreshCw className="w-3 h-3" /> Kirim ulang
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="flex items-center gap-1">
                                                            <Crown className="w-3 h-3 text-amber-400" />
                                                            <span className="text-white text-sm">{company.owner_name}</span>
                                                        </div>
                                                        <div className="text-xs text-slate-400">{company.owner_email}</div>
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-white">
                                                    <Users className="w-4 h-4 text-slate-400" />
                                                    {company.member_count}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-white">
                                                    <FolderKanban className="w-4 h-4 text-slate-400" />
                                                    {company.workspace_count}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`text-xs px-2 py-0.5 rounded ${
                                                    company.enabled_menus ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-300'
                                                }`}>
                                                    {company.enabled_menus ? `${menuCount} menus` : 'All'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => openWsModal(company)}
                                                        className="text-green-400 hover:text-green-300 hover:bg-slate-600"
                                                        title="Buat workspace untuk company">
                                                        <FolderKanban className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => {
                                                            setMemberModal({ open: true, company });
                                                            fetchMembers(company.id);
                                                        }}
                                                        className="text-blue-400 hover:text-blue-300 hover:bg-slate-600">
                                                        <Users className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => handleSyncMembers(company.id)}
                                                        disabled={syncingCompanyId === company.id}
                                                        className="text-amber-400 hover:text-amber-300 hover:bg-slate-600"
                                                        title="Sync workspace members ke company">
                                                        {syncingCompanyId === company.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => {
                                                            setEditForm({ name: company.name, description: company.description || '' });
                                                            setEditMenuToggles(enabledMenusToToggles(company.enabled_menus));
                                                            setEditModal({ open: true, company });
                                                        }}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-600">
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                        onClick={() => setDeleteModal({ open: true, company })}
                                                        className="text-red-400 hover:text-red-300 hover:bg-slate-600">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Create Modal */}
            <Dialog open={createModal} onOpenChange={setCreateModal}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Create Company</DialogTitle>
                        <DialogDescription className="text-slate-400">Create a new company workspace</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Company Name *</Label>
                            <Input value={createForm.name} onChange={(e) => setCreateForm(p => ({...p, name: e.target.value}))}
                                className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Enter company name" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={createForm.description} onChange={(e) => setCreateForm(p => ({...p, description: e.target.value}))}
                                className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Optional description" />
                        </div>
                        <div>
                            <Label>Owner Email *</Label>
                            <Input value={createForm.owner_email} onChange={(e) => setCreateForm(p => ({...p, owner_email: e.target.value}))}
                                className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="owner@email.com" type="email" />
                            <p className="text-xs text-slate-400 mt-1">Masukkan email user yang sudah terdaftar sebagai owner company</p>
                        </div>
                        <MenuToggles toggles={createMenuToggles} setToggles={setCreateMenuToggles} collapsed={collapsedSections} setCollapsed={setCollapsedSections} />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateModal(false)} className="text-slate-400">Cancel</Button>
                        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-black">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, company: null })}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Company</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Company Name</Label>
                            <Input value={editForm.name} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))}
                                className="bg-slate-700 border-slate-600 text-white mt-1" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={editForm.description} onChange={(e) => setEditForm(p => ({...p, description: e.target.value}))}
                                className="bg-slate-700 border-slate-600 text-white mt-1" />
                        </div>
                        <MenuToggles toggles={editMenuToggles} setToggles={setEditMenuToggles} collapsed={collapsedSections} setCollapsed={setCollapsedSections} />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditModal({ open: false, company: null })} className="text-slate-400">Cancel</Button>
                        <Button onClick={handleEdit} className="bg-amber-500 hover:bg-amber-600 text-black">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, company: null })}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-400" /> Delete Company
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            This will remove the company. Workspaces will become standalone. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteModal({ open: false, company: null })} className="text-slate-400">Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete}>Delete Company</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Members Modal */}
            <Dialog open={memberModal.open} onOpenChange={(open) => !open && setMemberModal({ open: false, company: null })}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-400" />
                            Members - {memberModal.company?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Add Member Section */}
                    <div className="border border-slate-700 rounded-lg p-3 mb-4">
                        <Label className="text-sm text-slate-400 mb-2 block">Add Member</Label>
                        <Input placeholder="Search users by name or email..." value={addMemberSearch}
                            onChange={(e) => setAddMemberSearch(e.target.value)}
                            className="bg-slate-700 border-slate-600 text-white mb-2" />
                        {addMemberSearch && (
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {availableUsers.slice(0, 5).map(u => (
                                    <div key={u.id} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-700 text-sm">
                                        <span>{u.name} <span className="text-slate-400">({u.email})</span></span>
                                        <Button size="sm" variant="ghost" onClick={() => handleAddMember(u.id)}
                                            className="text-green-400 hover:text-green-300 h-7">
                                            <UserPlus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                                {availableUsers.length === 0 && (
                                    <p className="text-sm text-slate-500 py-2 text-center">No users found</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Current Members */}
                    {loadingMembers ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
                    ) : (
                        <div className="max-h-64 overflow-y-auto space-y-2">
                            {members.map(m => (
                                <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-700/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-sm font-medium">
                                            {(m.user_name || '?')[0]?.toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-white flex items-center gap-1">
                                                {m.user_name || 'Unknown'}
                                                {m.role === 'owner' && <Crown className="w-3 h-3 text-amber-400" />}
                                            </div>
                                            <div className="text-xs text-slate-400">{m.user_email}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded ${m.role === 'owner' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600 text-slate-300'}`}>
                                            {m.role}
                                        </span>
                                        {m.role !== 'owner' && (
                                            <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(m.user_id)}
                                                className="text-red-400 hover:text-red-300 h-7">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Create Workspace Modal */}
            <Dialog open={wsModal.open} onOpenChange={(open) => !open && setWsModal({ open: false, company: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Buat Workspace{wsModal.company ? ` — ${wsModal.company.name}` : ''}</DialogTitle>
                        <DialogDescription>
                            Workspace dimiliki oleh owner company. Admin opsional bisa ditetapkan.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Nama Workspace *</Label>
                            <Input value={wsForm.name} onChange={(e) => setWsForm(p => ({ ...p, name: e.target.value }))}
                                className="mt-1" placeholder="Nama workspace" />
                        </div>
                        <div>
                            <Label>Deskripsi</Label>
                            <Input value={wsForm.description} onChange={(e) => setWsForm(p => ({ ...p, description: e.target.value }))}
                                className="mt-1" placeholder="Deskripsi opsional" />
                        </div>
                        <div>
                            <Label>Admin Workspace (Opsional)</Label>
                            <select
                                value={wsForm.admin_user_id}
                                onChange={(e) => setWsForm(p => ({ ...p, admin_user_id: e.target.value }))}
                                className="mt-1 w-full rounded-md bg-slate-700 border border-slate-600 text-white text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                            >
                                <option value="">— Tanpa admin —</option>
                                {wsMembers.filter(m => m.role !== 'owner').map(m => (
                                    <option key={m.user_id} value={m.user_id}>
                                        {m.user_name || 'Unknown'} ({m.user_email})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setWsModal({ open: false, company: null })}>Batal</Button>
                        <Button onClick={handleCreateWorkspace} disabled={creatingWs}
                            className="bg-amber-500 hover:bg-amber-600 text-black">
                            {creatingWs ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buat'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}